// ============================================================================
// Collections API Service
// All URLs resolved via ENDPOINTS (backed by APP_CONFIG). No port/type params.
// ============================================================================

import apiService from './service/apiService';
import ENDPOINTS from './endPoints';
import { APP_CONFIG } from '../config/app.config';
import type {
  Collection,
  CollectionsApiResponse,
  GenerateCollectionsPayload,
  GenerateCollectionsResponse,
  ManualCollectionPayload,
  ManualCollectionResponse,
  AddBundlePayload,
  CollectionMediaResponse,
  BatchCollectionMediaResponse,
} from '../types/collections.types';

type Headers = Record<string, string>;

// ---------------------------------------------------------------------------
// Shared error handler (production-safe — no console.log in prod)
// ---------------------------------------------------------------------------
function handleApiError(error: unknown, fallbackMsg: string): never {
  const err = error as any;
  const message = err?.message || fallbackMsg;
  if (import.meta.env.DEV) console.error(`❌ ${fallbackMsg}:`, message);
  throw new Error(message);
}

// ---------------------------------------------------------------------------
// Collections CRUD
// ---------------------------------------------------------------------------

/** Fetch all collections for the current user */
const fetchCollections = async (userId: string, headers?: Headers): Promise<Collection[]> => {
  try {
    const response = await apiService.get<CollectionsApiResponse>({
      endpoint: ENDPOINTS.getBundles(userId),
      headers,
    });
    const data = response.data;
    return data.bundles ?? data.data ?? (Array.isArray(data) ? data as unknown as Collection[] : []);
  } catch (error) {
    handleApiError(error, 'Failed to fetch collections');
  }
};

/** Generate collections via AI */
const generateCollections = async (
  payload: GenerateCollectionsPayload,
  headers?: Headers,
): Promise<GenerateCollectionsResponse> => {
  try {
    const response = await apiService.post<GenerateCollectionsResponse>({
      endpoint: ENDPOINTS.generateCollections,
      body: payload,
      headers,
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to generate collections');
  }
};

/** Create a manual collection */
const createManualCollection = async (
  payload: ManualCollectionPayload,
): Promise<ManualCollectionResponse> => {
  try {
    const response = await apiService.post<ManualCollectionResponse>({
      endpoint: ENDPOINTS.manualCollection,
      body: payload,
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to create manual collection');
  }
};

/** Add a new bundle */
const addBundle = async (payload: AddBundlePayload, headers?: Headers): Promise<any> => {
  try {
    const response = await apiService.post({ endpoint: ENDPOINTS.addBundle, body: payload, headers });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to add bundle');
  }
};

/** Edit an existing bundle */
const editBundle = async (
  payload: AddBundlePayload & { pk_id: string },
  headers?: Headers,
): Promise<any> => {
  try {
    const response = await apiService.put({ endpoint: ENDPOINTS.editBundle, body: payload, headers });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to edit bundle');
  }
};

/** Delete a bundle */
const deleteBundle = async (pkId: string, headers?: Headers): Promise<any> => {
  try {
    const response = await apiService.delete({ endpoint: ENDPOINTS.deleteBundle(pkId), headers });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to delete bundle');
  }
};

// ---------------------------------------------------------------------------
// Mediaflow APIs (native fetch — different base URL)
// ---------------------------------------------------------------------------

const jsonHeaders = (extra?: Headers): HeadersInit => ({
  'Content-Type': 'application/json',
  ...extra,
});

/** Fetch media for a single collection */
const fetchCollectionMedia = async (
  collectionId: string,
  headers?: Headers,
): Promise<CollectionMediaResponse> => {
  const res = await fetch(ENDPOINTS.mediaByCollection(collectionId), {
    method: 'GET',
    headers: jsonHeaders(headers),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

/** Fetch media for all user collections */
const fetchUserCollectionMedia = async (
  userId: string,
  headers?: Headers,
): Promise<BatchCollectionMediaResponse> => {
  const res = await fetch(ENDPOINTS.mediaByUser(userId), {
    method: 'GET',
    headers: jsonHeaders(headers),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

/** Regenerate image for a collection */
const regenerateImage = async (
  userId: string,
  collectionId: string,
  imageUrls: string[],
  headers?: Headers,
): Promise<{ success: boolean; message: string }> => {
  const res = await fetch(ENDPOINTS.imageGeneration, {
    method: 'POST',
    headers: jsonHeaders(headers),
    body: JSON.stringify({ userId, collectionId, imageUrls, productType: APP_CONFIG.TYPE }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

/** Regenerate video for a collection */
const regenerateVideo = async (
  userId: string,
  collectionId: string,
  headers?: Headers,
): Promise<{ success: boolean; message: string }> => {
  const res = await fetch(ENDPOINTS.videoGeneration, {
    method: 'POST',
    headers: jsonHeaders(headers),
    body: JSON.stringify({ userId, collectionId, productType: APP_CONFIG.TYPE }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
};

// ---------------------------------------------------------------------------
// Collection Filters & Product Search
// ---------------------------------------------------------------------------

export interface CollectionFiltersResponse {
  categories: string[];
  filters: Record<string, string[]>;
}

export interface CollectionProductSearchPayload {
  search_term: string;
  categories: string[];
  gender: string[];
}

export interface CollectionProduct {
  sku: string;
  lowercase_title: string;
  price: string;
  modified_price: number;
  image: string;
  // Primary category field (matches filter category names)
  category_name?: string;
  // New response fields
  description?: string;
  color?: string;
  material?: string;
  shape?: string;
  lifestyle?: string;
  department?: string;
  subclass_category?: string;
  groq_sub_category?: string;
  sku_type?: string;
  function?: string | null;
  type?: string | null;
  // Legacy fields (backward compatibility)
  occasion_list?: string[];
  gender?: string;
  category?: string;
  category_llm?: string;
  colors_string?: string;
}

export interface CollectionProductSearchResponse {
  products: CollectionProduct[];
  total_results: number;
}

/** Fetch available filter options (categories & genders) */
const fetchCollectionFilters = async (
  headers?: Headers,
): Promise<CollectionFiltersResponse> => {
  try {
    const response = await apiService.get<CollectionFiltersResponse>({
      endpoint: ENDPOINTS.collectionFilters,
      headers,
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to fetch collection filters');
  }
};

/** Search products for manual collection with filters */
const searchCollectionProducts = async (
  payload: CollectionProductSearchPayload,
  headers?: Headers,
): Promise<CollectionProductSearchResponse> => {
  try {
    const response = await apiService.post<CollectionProductSearchResponse>({
      endpoint: ENDPOINTS.collectionProductSearch,
      body: payload,
      headers,
    });
    return response.data;
  } catch (error) {
    handleApiError(error, 'Failed to search collection products');
  }
};

// ---------------------------------------------------------------------------
// Unified export
// ---------------------------------------------------------------------------

const collectionsApi = {
  fetchCollections,
  generateCollections,
  createManualCollection,
  addBundle,
  editBundle,
  deleteBundle,
  fetchCollectionMedia,
  fetchUserCollectionMedia,
  regenerateImage,
  regenerateVideo,
  fetchCollectionFilters,
  searchCollectionProducts,
};

export { createManualCollection };
export default collectionsApi;
